// ✅ src/pages/InstructorPage.js
import React, { useEffect, useState } from 'react';
import { getStorage, ref, listAll, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';

function InstructorPage() {
  const [students, setStudents] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [studentHistory, setStudentHistory] = useState([]);
  const [selectedHistoryStudent, setSelectedHistoryStudent] = useState('');

  useEffect(() => {
    fetchStudents();
    fetchLecturesFromStorage();
  }, []);

  const fetchStudents = async () => {
    const q = query(collection(db, 'users'), where('role', '==', 'student'));
    const snapshot = await getDocs(q);
    setStudents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const fetchLecturesFromStorage = async () => {
    const storage = getStorage();
    const baseRef = ref(storage, '강의/');
    const list = [];

    const traverse = async (folderRef) => {
      const res = await listAll(folderRef);
      for (const fileRef of res.items) {
        const url = await getDownloadURL(fileRef);
        list.push({ title: fileRef.name, url });
      }
      for (const folder of res.prefixes) {
        await traverse(folder);
      }
    };

    await traverse(baseRef);
    setLectures(list);
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const storage = getStorage();
    const fileRef = ref(storage, `강의/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    setUploading(true);

    uploadTask.on(
      'state_changed',
      (snap) => {
        const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
        setProgress(pct.toFixed(0));
      },
      (err) => {
        alert("❌ 업로드 실패: " + err.message);
        setUploading(false);
      },
      async () => {
        alert("✅ 업로드 완료!");
        setUploading(false);
        setProgress(0);
        await fetchLecturesFromStorage();
      }
    );
  };

  const handleAssign = async () => {
    if (!selectedLecture || !selectedStudent) {
      alert("강의와 학생을 선택하세요.");
      return;
    }

    try {
      await addDoc(collection(db, 'lectureAssignments'), {
        userId: selectedStudent,
        lectureUrl: selectedLecture.url,
        lectureTitle: selectedLecture.title.replace('.MP4', ''),
        assignedAt: new Date()
      });
      alert('✅ 강의가 배정되었습니다.');
    } catch (e) {
      alert('❌ 배정 실패: ' + e.message);
    }
  };

  const handleShowHistory = async (studentId) => {
    setSelectedHistoryStudent(studentId);
    const q = query(collection(db, 'histories'), where('userId', '==', studentId));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => doc.data());
    setStudentHistory(data);
  };

  return (
    <div style={{ maxWidth: '960px', margin: '40px auto', padding: '20px' }}>
      <Header role="instructor" />

      <h1 style={{ fontSize: '1.8rem', marginBottom: '20px' }}>📚 강사 관리 페이지</h1>

      {/* 업로드 */}
      <section style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '12px', marginBottom: '40px', border: '1px solid #ddd' }}>
        <h2 style={{ marginBottom: '12px' }}>🎥 강의 영상 업로드</h2>
        <input type="file" accept="video/*" onChange={handleUpload} disabled={uploading} />
        {uploading && <p style={{ marginTop: '8px' }}>업로드 중: {progress}%</p>}
      </section>

      {/* 배정 */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ marginBottom: '12px' }}>📌 강의 배정</h2>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <select
            value={selectedLecture?.url || ''}
            onChange={(e) => {
              const found = lectures.find(l => l.url === e.target.value);
              setSelectedLecture(found);
            }}
            style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
          >
            <option value="">-- 강의 선택 --</option>
            {lectures.map((lec, idx) => (
              <option key={idx} value={lec.url}>{lec.title}</option>
            ))}
          </select>

          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
          >
            <option value="">-- 학생 선택 --</option>
            {students.map((stu) => (
              <option key={stu.id} value={stu.id}>
                {stu.name || '(이름 없음)'} ({stu.id})
              </option>
            ))}
          </select>

          <button
            onClick={handleAssign}
            style={{ padding: '10px 16px', backgroundColor: '#00704A', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            배정하기
          </button>
        </div>
      </section>

      {/* 수강 이력 */}
      <section>
        <h2 style={{ marginBottom: '12px' }}>📜 수강 이력 조회</h2>
        <ul style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', paddingLeft: 0, marginBottom: '16px' }}>
          {students.map((stu) => (
            <li key={stu.id}>
              <button
                onClick={() => handleShowHistory(stu.id)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#eee',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  cursor: 'pointer'
                }}
              >
                {stu.name || '학생'} ({stu.id})
              </button>
            </li>
          ))}
        </ul>

        {selectedHistoryStudent && (
          <div style={{ marginTop: '20px' }}>
            <h4>📖 {selectedHistoryStudent} 학생의 수강 이력</h4>
            {studentHistory.length === 0 ? (
              <p>수강 이력이 없습니다.</p>
            ) : (
              <ul style={{ lineHeight: '1.6' }}>
                {studentHistory.map((h, idx) => (
                  <li key={idx}>
                    {h.lectureTitle || h.lectureUrl.split('/').pop()} - {new Date(h.watchedAt).toLocaleString()}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default InstructorPage;
